import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaPaperPlane, FaMapMarkerAlt, FaPhone, FaEnvelope, FaGlobe, FaFacebook, FaInstagram } from 'react-icons/fa';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    phone: '',
    message: ''
  });
  
  const [formStatus, setFormStatus] = useState({
    submitted: false,
    success: false,
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      setFormStatus({
        submitted: true,
        success: false,
        message: 'Please fill out all required fields.'
      });
      return;
    }
    
    // Form would be submitted to backend here
    // For demo, we'll simulate a successful submission
    setFormStatus({
      submitted: true,
      success: true,
      message: 'Your message has been sent successfully!'
    });
    
    // Reset form after successful submission
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        subject: '',
        phone: '',
        message: ''
      });
      setFormStatus({
        submitted: false,
        success: false,
        message: ''
      });
    }, 3000);
  };

  return (
    <section id="contact" className="py-16 bg-gradient-to-br from-gray-50 via-white to-blue-50 min-h-[60vh]">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-extrabold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-pink-400 to-yellow-400 animate-float">
            Contact <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500">Us</span>
          </h2>
          <div className="w-32 h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-full animate-pulse mx-auto mb-4"></div>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-stretch">
          {/* Google Map */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="w-full h-[400px] bg-white/60 rounded-2xl shadow-xl overflow-hidden backdrop-blur-md border border-white/40"
          >
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3963.3882378965777!2d79.8998116142675!3d6.863840221018639!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ae25bc4fdad0983%3A0xafca31fc6dbc1641!2sStem+Labs+-+Institute+of+STEM+Education!5e0!3m2!1sen!2slk!4v1566380028263!5m2!1sen!2slk" 
              width="100%" 
              height="100%" 
              style={{ border: 0 }} 
              allowFullScreen="" 
              loading="lazy"
              title="STEM Labs Location"
              className="rounded-2xl w-full h-full"
            ></iframe>
          </motion.div>
          
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col justify-between"
          >
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl p-8 border border-white/40 mb-6 flex-1 flex flex-col justify-between">
              <h4 className="text-2xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500">Send Us A Message</h4>
              
              {formStatus.submitted && (
                <div className={`mb-4 p-3 rounded ${formStatus.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {formStatus.message}
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input 
                    type="text" 
                    name="name" 
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter Name" 
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-theme-accent bg-white/80"
                    required
                  />
                  <input 
                    type="email" 
                    name="email" 
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter Email" 
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-theme-accent bg-white/80"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input 
                    type="text" 
                    name="subject" 
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="Enter Subject" 
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-theme-accent bg-white/80"
                  />
                  <input 
                    type="text" 
                    name="phone" 
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Enter Phone" 
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-theme-accent bg-white/80"
                  />
                </div>
                
                <textarea 
                  name="message" 
                  value={formData.message}
                  onChange={handleChange}
                  rows="4" 
                  placeholder="Enter Message" 
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-theme-accent bg-white/80"
                  required
                ></textarea>
                
                <div className="flex space-x-4 mt-2">
                  <button 
                    type="submit" 
                    className="flex items-center px-6 py-2 rounded-full font-semibold bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white shadow-lg hover:from-pink-500 hover:to-indigo-500 transition-all duration-300"
                  >
                    <FaPaperPlane className="mr-2" /> Send Message
                  </button>
                  <button 
                    type="reset" 
                    className="px-6 py-2 rounded-full font-semibold bg-gray-700 text-white shadow-lg hover:bg-gray-600 transition-colors"
                    onClick={() => setFormData({
                      name: '',
                      email: '',
                      subject: '',
                      phone: '',
                      message: ''
                    })}
                  >
                    Reset
                  </button>
                </div>
              </form>
            </div>
            
            {/* Contact Information */}
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl p-6 border border-white/40 mt-2">
              <h5 className="text-lg font-semibold mb-3 bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500">Contact Information</h5>
              <div className="space-y-3 text-gray-700">
                <div className="flex items-center"><FaMapMarkerAlt className="mr-2 text-theme-accent" /> No 137/17, Old Kesbewa Rd, Gangodawila, Nugegoda, Sri Lanka</div>
                <div className="flex items-center"><FaPhone className="mr-2 text-theme-accent" /> 071 2407487 / 077 908 3607</div>
                <div className="flex items-center"><FaEnvelope className="mr-2 text-theme-accent" /> <a href="mailto:info@stemlabs.edu.lk" className="hover:text-theme-accent transition-colors">info@stemlabs.edu.lk</a> / <a href="mailto:stemlabs.edu@gmail.com" className="hover:text-theme-accent transition-colors">stemlabs.edu@gmail.com</a></div>
                <div className="flex items-center"><FaGlobe className="mr-2 text-theme-accent" /> <a href="https://stemlabs.edu.lk" className="hover:text-theme-accent transition-colors">stemlabs.edu.lk</a></div>
              </div>
              <div className="flex space-x-3 mt-4">
                <a href="https://www.facebook.com/STEMLabSL/" target="_blank" rel="noopener noreferrer" className="bg-blue-600 text-white p-3 rounded-full hover:bg-blue-700 transition-colors" aria-label="Facebook"><FaFacebook /></a>
                <a href="https://www.instagram.com/_stemlabs.edu.lk_/" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-tr from-purple-600 via-pink-500 to-orange-400 text-white p-3 rounded-full hover:opacity-90 transition-opacity" aria-label="Instagram"><FaInstagram /></a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection; 
 